import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft, 
  Users, 
  DollarSign, 
  TrendingUp, 
  Plus, 
  Trash2, 
  Edit, 
  Eye,
  Copy,
  CheckCircle
} from 'lucide-react';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AffiliatesAdminPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [affiliates, setAffiliates] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [sales, setSales] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    code: '',
    commission_percent: 10,
    discount_percent: 10,
    active: true
  });

  const fetchAffiliates = async () => {
    try {
      setLoading(true);
      const [affiliatesRes, statsRes] = await Promise.all([
        axios.get(`${API}/affiliates`),
        axios.get(`${API}/affiliates/dashboard/stats`)
      ]);
      setAffiliates(affiliatesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los afiliados',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const handleCreate = () => {
    setSelectedAffiliate(null);
    setFormData({
      name: '',
      email: '',
      code: '',
      commission_percent: 10,
      discount_percent: 10,
      active: true
    });
    setIsModalOpen(true);
  };

  const handleEdit = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setFormData({
      name: affiliate.name,
      email: affiliate.email,
      code: affiliate.code,
      commission_percent: affiliate.commission_percent,
      discount_percent: affiliate.discount_percent,
      active: affiliate.active
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (affiliateId) => {
    if (!window.confirm('¿Estás segura de eliminar este afiliado?')) return;
    
    try {
      await axios.delete(`${API}/affiliates/${affiliateId}`);
      toast({
        title: 'Éxito',
        description: 'Afiliado eliminado correctamente'
      });
      fetchAffiliates();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el afiliado',
        variant: 'destructive'
      });
    }
  };

  const handleViewSales = async (affiliate) => {
    try {
      const response = await axios.get(`${API}/affiliates/${affiliate.id}/sales`);
      setSales(response.data);
      setSelectedAffiliate(affiliate);
      setSalesModalOpen(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las ventas',
        variant: 'destructive'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedAffiliate) {
        await axios.put(`${API}/affiliates/${selectedAffiliate.id}`, {
          name: formData.name,
          email: formData.email,
          commission_percent: formData.commission_percent,
          discount_percent: formData.discount_percent,
          active: formData.active
        });
        toast({
          title: 'Éxito',
          description: 'Afiliado actualizado correctamente'
        });
      } else {
        await axios.post(`${API}/affiliates`, formData);
        toast({
          title: 'Éxito',
          description: `Afiliado creado con código: ${formData.code.toUpperCase()}`
        });
      }
      setIsModalOpen(false);
      fetchAffiliates();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Error al guardar',
        variant: 'destructive'
      });
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const generateCode = () => {
    const name = formData.name.split(' ')[0].toUpperCase();
    setFormData(prev => ({ ...prev, code: `${name}10` }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Volver</span>
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  Panel de Afiliados
                </h1>
                <p className="text-purple-200 text-sm">Gestiona tus colaboraciones con influencers</p>
              </div>
            </div>
            
            <Button
              onClick={handleCreate}
              className="bg-white text-purple-600 hover:bg-purple-50"
              data-testid="create-affiliate-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Afiliado
            </Button>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-sm">Total Afiliados</p>
                <p className="text-3xl font-bold">{stats.total_affiliates || 0}</p>
              </div>
              <Users className="w-10 h-10 opacity-50" />
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-200 text-sm">Ventas Totales</p>
                <p className="text-3xl font-bold">€{(stats.total_sales || 0).toFixed(2)}</p>
              </div>
              <TrendingUp className="w-10 h-10 opacity-50" />
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-200 text-sm">Comisiones Totales</p>
                <p className="text-3xl font-bold">€{(stats.total_commissions || 0).toFixed(2)}</p>
              </div>
              <DollarSign className="w-10 h-10 opacity-50" />
            </div>
          </Card>
          
          <Card className="p-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-200 text-sm">Saldo Pendiente</p>
                <p className="text-3xl font-bold">€{(stats.pending_payouts || 0).toFixed(2)}</p>
              </div>
              <DollarSign className="w-10 h-10 opacity-50" />
            </div>
          </Card>
        </div>

        {/* Affiliates Table */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="text-lg font-semibold">Lista de Afiliados</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : affiliates.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay afiliados registrados</p>
              <Button onClick={handleCreate} variant="link" className="mt-2">
                Crear el primero
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Influencer</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead className="text-center">Descuento</TableHead>
                  <TableHead className="text-center">Comisión</TableHead>
                  <TableHead className="text-right">Ventas</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {affiliates.map((affiliate) => (
                  <TableRow key={affiliate.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{affiliate.name}</p>
                        <p className="text-sm text-gray-500">{affiliate.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="bg-purple-100 text-purple-700 px-2 py-1 rounded font-mono text-sm">
                          {affiliate.code}
                        </code>
                        <button
                          onClick={() => copyCode(affiliate.code)}
                          className="text-gray-400 hover:text-purple-600"
                          title="Copiar código"
                        >
                          {copiedCode === affiliate.code ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">
                        {affiliate.discount_percent}%
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm">
                        {affiliate.commission_percent}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div>
                        <p className="font-medium">€{(affiliate.total_sales || 0).toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{affiliate.sales_count || 0} ventas</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium text-green-600">
                        €{(affiliate.available_balance || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`px-2 py-1 rounded text-xs ${
                        affiliate.active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {affiliate.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewSales(affiliate)}
                          title="Ver ventas"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(affiliate)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(affiliate.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedAffiliate ? 'Editar Afiliado' : 'Nuevo Afiliado'}
            </DialogTitle>
            <DialogDescription>
              {selectedAffiliate 
                ? 'Actualiza los datos del influencer'
                : 'Añade un nuevo influencer como afiliado'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre del Influencer *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="María García"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="maria@email.com"
                required
              />
            </div>
            
            {!selectedAffiliate && (
              <div>
                <Label htmlFor="code">Código de Descuento *</Label>
                <div className="flex gap-2">
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="MARIA10"
                    className="uppercase"
                    required
                  />
                  <Button type="button" variant="outline" onClick={generateCode}>
                    Auto
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Este será el código que compartirá el influencer
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="discount">Descuento Cliente (%)</Label>
                <Input
                  id="discount"
                  type="number"
                  min="1"
                  max="50"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) }))}
                />
              </div>
              
              <div>
                <Label htmlFor="commission">Comisión Influencer (%)</Label>
                <Input
                  id="commission"
                  type="number"
                  min="1"
                  max="50"
                  value={formData.commission_percent}
                  onChange={(e) => setFormData(prev => ({ ...prev, commission_percent: parseFloat(e.target.value) }))}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="active">Estado Activo</Label>
              <Switch
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                {selectedAffiliate ? 'Guardar Cambios' : 'Crear Afiliado'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sales History Modal */}
      <Dialog open={salesModalOpen} onOpenChange={setSalesModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Historial de Ventas - {selectedAffiliate?.name}
            </DialogTitle>
            <DialogDescription>
              Código: <code className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{selectedAffiliate?.code}</code>
            </DialogDescription>
          </DialogHeader>
          
          {sales.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p>No hay ventas registradas con este código</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-right">Venta</TableHead>
                    <TableHead className="text-right">Comisión</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sales.map((sale) => (
                    <TableRow key={sale.id}>
                      <TableCell>
                        {new Date(sale.created_at).toLocaleDateString('es-ES', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell className="text-right">€{sale.sale_amount.toFixed(2)}</TableCell>
                      <TableCell className="text-right text-green-600 font-medium">
                        +€{sale.commission_amount.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <div className="pt-4 border-t">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Saldo disponible:</span>
              <span className="text-2xl font-bold text-green-600">
                €{(selectedAffiliate?.available_balance || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster />
      <Footer />
    </div>
  );
};

export default AffiliatesAdminPage;
